import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Card, Col, Row, Select, Space, Form, DatePicker, Spin, Tag, Empty } from "antd";
import styled from "styled-components";
import Stack from "react-bootstrap/Stack";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  PieChart,
  Pie,
  Cell,
  Tooltip,
  Legend,
  ResponsiveContainer,
  LabelList,
} from "recharts";
import { useSelector } from "react-redux";
import dayjs from "dayjs";
import upload from "../../../assets/images/upload.svg";
import { StyledTable } from "../../../Reusablecomponents/UITable/UITable";
import { CALL_API } from "../../../Common/api";
import { handleDownloadChart } from "../../../Reusablecomponents/Export/Exportpdf";
import { withNexusReportShell } from "../NexusReportShell";

// Nexus Fuel Report: same page and charts as the AI Fuel Report, built from the
// Nexus fuel entries (POST nexus/fuelreport), plus the TraKing telemetry fuel
// data - consumption, efficiency, refuelling and drains (POST
// nexus/nexusfuelinsights).

const { Option } = Select;
const { RangePicker } = DatePicker;

const Fuelreportstyle = styled.div`
  .head {
    color: #5f6d8c;
  }
  .total {
    display: flex;
    align-items: center;
    justify-content: space-between !important;
    flex-wrap: wrap;
    gap: 8px;
  }
  .Custom {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 12px 20px;
  }
  .dropstyle {
    margin-top: 20px !important;
    width: 251px !important;
  }
  .dropstyle1 {
    height: 36px;
    width: 251px !important;
  }
  .fuel-tiles {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
    gap: 12px;
  }
  .fuel-tile {
    background: #fff;
    border: 1px solid #f0f0f0;
    border-radius: 10px;
    padding: 14px 16px;
    text-align: center;
  }
  .fuel-tile .label {
    color: #5f6d8c;
    font-size: 13px;
  }
  .fuel-tile .value {
    color: #363636;
    font-size: 24px;
    font-weight: 600;
    line-height: 1.3;
  }
  .fuel-tile .unit {
    color: #5f6d8c;
    font-size: 12px;
    font-weight: 400;
  }
  .section-title {
    color: #5f6d8c;
    font-weight: 500;
    margin: 4px 0 10px;
  }
  .legend-list {
    display: flex;
    flex-direction: column;
    gap: 6px;
    padding: 4px 8px 12px;
  }
  .legend-row {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 13px;
  }
  .legend-row .dot {
    width: 12px;
    height: 12px;
    border-radius: 2px;
    flex: none;
  }
  .legend-row .name {
    flex: 1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  @media (max-width: 767px) {
    .dropstyle,
    .dropstyle1 {
      width: 100% !important;
      margin-top: 0 !important;
    }
    .dropstyle .ant-select,
    .dropstyle1 .ant-picker,
    .dropstyle1 .ant-space {
      width: 100% !important;
    }
    .Custom {
      width: 100%;
    }
    .fuel-tile .value {
      font-size: 20px;
    }
    .ant-card-body {
      padding: 12px;
    }
  }
  .plc .ant-select-selection-placeholder {
    color: black !important;
  }
  .plc .ant-select-arrow {
    color: black;
  }
  .fuel-report-item {
    display: flex;
    align-items: center;
    justify-content: center;
    padding-top: 10px;
  }
  .text-content {
    padding: 20px;
    line-height: 15px;
    text-align: center;
  }
  .vr {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 1px;
    background-color: #000;
    margin-left: 250px;
  }
  @media (max-width: 767px) {
    .vr {
      display: none;
    }
    .fuel-totals {
      flex-direction: column;
    }
  }
  .ant-card-head {
    border-bottom: none;
  }
  .Fuel-List {
    border: 1px solid #f4f4f4;
    border-radius: 5px;
    margin: 5px;
  }
`;

const COLORS = ["#73B03A", "#827EFF", "#FFFD69", "#FFAD6F", "#F870FF", "#FFD361", "#EB7283", "#61FFE5", "#FF4F52", "#95FF87"];
const COLORS1 = ["#FFD361", "#EB7283", "#61FFE5", "#FF4F52", "#95FF87", "#73B03A", "#827EFF", "#FFFD69", "#FFAD6F", "#F870FF"];

const FuelsColumn = [{ title: "Fuel Station", dataIndex: "DATA", key: "DATA" }];

const Duration = [
  { label: "Current Month", value: "current_month" },
  { label: "Current Year", value: "current_year" },
  { label: "Last Month", value: "last_month" },
  { label: "Today", value: "today" },
];

const rangeFor = (duration) => {
  if (duration === "today") return [dayjs(), dayjs()];
  if (duration === "last_month")
    return [dayjs().subtract(1, "month").startOf("month"), dayjs().subtract(1, "month").endOf("month")];
  if (duration === "current_year") return [dayjs().startOf("year"), dayjs().endOf("year")];
  return [dayjs().startOf("month"), dayjs().endOf("month")];
};

const formatNumber = (number) => new Intl.NumberFormat("en-US").format(number || 0);
const fmt1 = (n) => (n == null || Number.isNaN(Number(n)) ? "-" : Number(n).toLocaleString("en-US", { maximumFractionDigits: 1 }));

const useIsMobile = (breakpoint = 768) => {
  const [mobile, setMobile] = useState(typeof window !== "undefined" && window.innerWidth < breakpoint);
  useEffect(() => {
    const onResize = () => setMobile(window.innerWidth < breakpoint);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [breakpoint]);
  return mobile;
};

const mapLink = (lat, lng) =>
  lat != null && lng != null ? (
    <a href={`https://www.google.com/maps?q=${lat},${lng}`} target="_blank" rel="noreferrer">
      {Number(lat).toFixed(4)}, {Number(lng).toFixed(4)}
    </a>
  ) : (
    "-"
  );

// donut label: "<name> (<litres> L)" at the end of a leader line
const makeLabel = (colors, text, fontSize = 14) =>
  function DonutLabel({ cx, cy, midAngle, innerRadius, outerRadius, index }) {
    const RADIAN = Math.PI / 180;
    const radius = innerRadius + (outerRadius - innerRadius) * 1.25;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);
    const ishrline = Math.cos(-midAngle * RADIAN) > 0;
    const lineLength = ishrline ? 100 : -100;
    const textOffset = ishrline ? 20 : -20;
    const squareSize = 12;
    return (
      <>
        <line x1={x} y1={y} x2={x + lineLength} y2={y} stroke="#ccc" strokeWidth={1} />
        <rect
          x={x + lineLength + (ishrline ? -squareSize - 0 : 0)}
          y={y - squareSize / 2}
          width={squareSize}
          height={squareSize}
          fill={colors[index % colors.length]}
        />
        <text
          x={x + lineLength + textOffset}
          y={y}
          fill="#5F6D8C"
          textAnchor={ishrline ? "start" : "end"}
          dominantBaseline="central"
          fontSize={fontSize}
        >
          {text(index)}
        </text>
      </>
    );
  };

const NexusFuelReport = () => {
  const reduxPermissions = useSelector((state) => state?.authTracking?.myPermissionList || {});
  const exportAllowed = useMemo(
    () => Array.isArray(reduxPermissions?.NE_FUELRPT) && reduxPermissions.NE_FUELRPT.includes("EX"),
    [reduxPermissions],
  );

  const [duration, setDuration] = useState("current_month");
  const [selectedDate, setSelectedDate] = useState(rangeFor("current_month"));
  const [report, setReport] = useState({});
  const [loading, setLoading] = useState(false);
  const [insights, setInsights] = useState(null);
  const [insightsLoading, setInsightsLoading] = useState(false);
  const [insightsError, setInsightsError] = useState("");
  const isMobile = useIsMobile();

  useEffect(() => {
    setSelectedDate(rangeFor(duration));
  }, [duration]);

  const getFuelReport = useCallback(async () => {
    if (!selectedDate?.[0] || !selectedDate?.[1]) return;
    setLoading(true);
    try {
      const { data } = await CALL_API("POST", "nexus/fuelreport", {
        dateFrom: dayjs(selectedDate[0]).format("YYYY-MM-DD"),
        dateTo: dayjs(selectedDate[1]).format("YYYY-MM-DD"),
      });
      setReport(data || {});
    } catch (error) {
      console.error("[NexusFuelReport] fetch error:", error);
    } finally {
      setLoading(false);
    }
  }, [selectedDate]);

  useEffect(() => {
    getFuelReport();
  }, [getFuelReport]);

  // TraKing telemetry fuel data for the same date range
  const getInsights = useCallback(async () => {
    if (!selectedDate?.[0] || !selectedDate?.[1]) return;
    setInsightsLoading(true);
    setInsightsError("");
    try {
      const res = await CALL_API("POST", "nexus/nexusfuelinsights", {
        dateFrom: dayjs(selectedDate[0]).format("YYYY-MM-DD"),
        dateTo: dayjs(selectedDate[1]).format("YYYY-MM-DD"),
      });
      if (res?.ResponseCode && res.ResponseCode !== 200) {
        setInsights(null);
        setInsightsError(res?.Message || res?.message || "Unable to load the telemetry fuel data.");
      } else {
        setInsights(res?.data || null);
      }
    } catch (error) {
      console.error("[NexusFuelReport] insights error:", error);
      setInsights(null);
      setInsightsError("Unable to load the telemetry fuel data.");
    } finally {
      setInsightsLoading(false);
    }
  }, [selectedDate]);

  useEffect(() => {
    getInsights();
  }, [getInsights]);

  const list = (v) => (Array.isArray(v) ? v : []);
  const vehicleData = list(report?.FuelConsumed);
  const driverData = list(report?.FuelConsumedDriver);
  const stationChartData = list(report?.FuelConsumedStation);
  const stationData = list(report?.FuelConsumedAllStation).map((s, i) => ({ ...s, key: i }));

  const FuelDetailsData = [
    { id: "1", Name: "Total Litres Fueled", values: formatNumber(report?.TotalFuel?.FuelQty) },
    { id: "2", Name: "Total Cost For Fuel", values: `${formatNumber(report?.TotalFuelAmount?.FuelQty)} KES` },
  ];

  const vehicleLabel = isMobile ? false : makeLabel(COLORS, (i) => `${vehicleData[i]?.VEHNAME || ""} (${vehicleData[i]?.FuelQty} L)`);
  const driverLabel = isMobile ? false : makeLabel(COLORS1, (i) => `${driverData[i]?.DATA || ""} (${driverData[i]?.VAL} L)`);

  // on narrow screens the leader-line labels do not fit: list them under the chart
  const legendList = (data, nameKey, valueKey, colors) =>
    isMobile && data.length > 0 ? (
      <div className="legend-list">
        {data.map((d, i) => (
          <div className="legend-row" key={i}>
            <span className="dot" style={{ background: colors[i % colors.length] }} />
            <span className="name">{d[nameKey]}</span>
            <strong>{d[valueKey]} L</strong>
          </div>
        ))}
      </div>
    ) : null;

  const ins = insights || {};
  const sm = ins.summary || {};
  const insVehicles = (ins.vehicles || []).map((v) => ({ ...v, key: v.asset_id }));
  const insEvents = (ins.events || []).map((e, i) => ({ ...e, key: `${e.asset_id}-${e.alertTime}-${i}` }));
  const consumptionChart = insVehicles
    .filter((v) => v.fuelConsumed > 0 || v.refueled > 0 || v.drained > 0)
    .map((v) => ({ name: v.asset_name, Consumed: v.fuelConsumed, Refuelled: v.refueled, Drained: v.drained }));
  const tiles = [
    { label: "Fuel Consumed", value: fmt1(sm.fuelConsumed), unit: "L" },
    { label: "Fuel Efficiency", value: fmt1(sm.fuelEfficiency), unit: "km/L" },
    { label: "Refuelled", value: fmt1(sm.refueled), unit: "L" },
    { label: "Fuel Drained", value: fmt1(sm.drained), unit: "L" },
    { label: "Distance", value: fmt1(sm.totalDistance), unit: "km" },
    { label: "Refuel Events", value: sm.refuelEvents ?? "-", unit: "" },
    { label: "Fuel Drain Events", value: sm.drainEvents ?? "-", unit: "" },
  ];
  const vehicleColumns = [
    { title: "Vehicle", dataIndex: "asset_name", key: "asset_name", fixed: isMobile ? undefined : "left", sorter: (a, b) => String(a.asset_name).localeCompare(String(b.asset_name)) },
    { title: "Tank (L)", dataIndex: "tankCapacity", key: "tankCapacity", render: (v) => (v ? fmt1(v) : "-") },
    {
      title: "Remaining",
      key: "remaining",
      render: (_, r) => (r.activeDays > 0 ? `${fmt1(r.remaining)} L (${fmt1(r.remainingPercent)}%)` : "-"),
    },
    { title: "Distance (km)", dataIndex: "totalDistance", key: "totalDistance", sorter: (a, b) => a.totalDistance - b.totalDistance, render: fmt1 },
    { title: "Consumed (L)", dataIndex: "fuelConsumed", key: "fuelConsumed", sorter: (a, b) => a.fuelConsumed - b.fuelConsumed, render: fmt1 },
    { title: "Efficiency (km/L)", dataIndex: "fuelEfficiency", key: "fuelEfficiency", sorter: (a, b) => (a.fuelEfficiency || 0) - (b.fuelEfficiency || 0), render: fmt1 },
    { title: "Refuelled (L)", dataIndex: "refueled", key: "refueled", sorter: (a, b) => a.refueled - b.refueled, render: fmt1 },
    { title: "Drained (L)", dataIndex: "drained", key: "drained", sorter: (a, b) => a.drained - b.drained, render: (v) => (v > 0 ? <span style={{ color: "#CF1322", fontWeight: 600 }}>{fmt1(v)}</span> : "—") },
    { title: "Days with data", key: "days", render: (_, r) => `${r.activeDays}/${r.totalDays}` },
  ];
  const eventColumns = [
    { title: "Date & Time", dataIndex: "alertTime", key: "alertTime", render: (v) => dayjs(v).format("DD MMM YYYY, hh:mm A") },
    { title: "Vehicle", dataIndex: "asset_name", key: "asset_name" },
    {
      title: "Event",
      dataIndex: "type",
      key: "type",
      render: (v) => <Tag color={v === "Refuel" ? "green" : "red"}>{v === "Refuel" ? "Refuel" : "Fuel Drain"}</Tag>,
    },
    { title: "Litres", dataIndex: "litres", key: "litres", render: (v) => (v ? `${fmt1(v)} L` : "-") },
    {
      title: "Detected By",
      dataIndex: "source",
      key: "source",
      render: (v, r) => (r.type === "Refuel" ? "Refuel alert" : v === "reading" ? "Fuel Drained (Detected)" : "Fuel Stolen Alert"),
    },
    { title: "Location", key: "loc", render: (_, r) => mapLink(r.latitude, r.longitude) },
  ];

  const exportIcon = (id, title) => (
    <img
      src={upload}
      alt="Upload Icon"
      className="hover-image"
      style={{ cursor: exportAllowed ? "pointer" : "not-allowed" }}
      onClick={() => {
        if (!exportAllowed) return;
        handleDownloadChart(id, title);
      }}
    />
  );

  const FuelVerticalChart = () => (
    <ResponsiveContainer width="100%" height={isMobile ? 300 : 380}>
      <BarChart layout="vertical" margin={{ top: 20, right: 30, left: isMobile ? 0 : 20, bottom: 10 }} barSize={isMobile ? 20 : 28} data={stationChartData}>
        <CartesianGrid vertical={false} horizontal={false} />
        <XAxis type="number" axisLine={false} tickLine={false} />
        <YAxis
          type="category"
          dataKey="DATA"
          axisLine={false}
          tickLine={true}
          width={isMobile ? 80 : 60}
          tick={{ fontSize: 12, textAnchor: "end" }}
          label={isMobile ? undefined : {
            value: "Litres",
            angle: 0,
            position: "bottom",
            offset: 8,
            dx: 5,
            style: { fontSize: 14, fill: "#000" },
          }}
        />
        <Tooltip />
        <Bar dataKey="VAL" fill="#126DF6" radius={5} style={{ cursor: "pointer" }}>
          <LabelList
            content={({ x, y, width, value }) => (
              <text x={x + width + 5} y={y + 14} fill="#000" fontSize={12}>
                {Math.round(value)}
              </text>
            )}
          />
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );

  const donut = (data, dataKey, colors, label, outer, inner, fontSize) => (
    <ResponsiveContainer width="100%" height={isMobile ? 200 : 300}>
      <PieChart width="100%" height={400}>
        <Pie
          dataKey={dataKey}
          startAngle={180}
          endAngle={0}
          data={data}
          cx="50%"
          cy="80%"
          outerRadius={isMobile ? Math.round(outer * 0.55) : outer}
          innerRadius={isMobile ? Math.round(inner * 0.55) : inner}
          label={label}
        >
          {data.map((entry, index) => (
            <Cell key={`cell-${index}`} fill={colors[index % colors.length]} />
          ))}
        </Pie>
        <text x="50%" y="70%" textAnchor="middle" dominantBaseline="middle" fontSize={16} fill="#5F6D8C">
          <tspan x="50%" dy="1rem" style={{ fontSize: isMobile ? "20px" : fontSize, fontWeight: "bold" }}>
            Top 10
          </tspan>
        </text>
      </PieChart>
    </ResponsiveContainer>
  );

  return (
    <>
      <Fuelreportstyle>
        <Row gutter={12}>
          <Col sm={24} xl={24} md={24}>
            <Card>
              <div className="total">
                <div>Overview</div>
                <div className="Custom">
                  <div className="dropstyle">
                    <Form.Item label="" name="dropdown">
                      <Select style={{ width: "240px" }} value={duration} onChange={setDuration}>
                        {Duration.map((item) => (
                          <Option key={item.value} value={item.value}>
                            {item?.label}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </div>
                  <div className="dropstyle1">
                    <Space direction="vertical" size={12}>
                      <RangePicker value={selectedDate} onChange={(date) => date && setSelectedDate(date)} />
                    </Space>
                  </div>
                </div>
              </div>
            </Card>
          </Col>
          {/* <Col sm={24} md={24} className="mt-3">
            <Spin spinning={insightsLoading}>
              <Card bordered={true}>
                <div className="section-title">Fuel Telemetry (TraKing)</div>
                {insightsError ? (
                  <Empty description={insightsError} />
                ) : (
                  <div className="fuel-tiles">
                    {tiles.map((t) => (
                      <div className="fuel-tile" key={t.label}>
                        <div className="label">{t.label}</div>
                        <div className="value">
                          {t.value} <span className="unit">{t.unit}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </Card>
            </Spin>
          </Col>
          {!insightsError && (
            <>
              <Col sm={24} md={24} className="mt-3">
                <Spin spinning={insightsLoading}>
                  <div id="Export Chart3">
                    <Card
                      title="Consumption, Refuelling & Drains by Vehicle (L)"
                      bordered={true}
                      extra={exportIcon("Export Chart3", "Fuel Consumption by Vehicle")}
                    >
                      {consumptionChart.length === 0 ? (
                        <Empty description="No fuel telemetry for this period" />
                      ) : (
                        <ResponsiveContainer width="100%" height={isMobile ? 280 : 340}>
                          <BarChart data={consumptionChart} margin={{ top: 10, right: 10, left: -10, bottom: isMobile ? 40 : 10 }}>
                            <CartesianGrid vertical={false} strokeDasharray="3 3" />
                            <XAxis dataKey="name" tick={{ fontSize: 12 }} interval={0} angle={isMobile ? -35 : 0} textAnchor={isMobile ? "end" : "middle"} height={isMobile ? 60 : 30} />
                            <YAxis tick={{ fontSize: 12 }} />
                            <Tooltip />
                            <Legend />
                            <Bar dataKey="Consumed" fill="#126DF6" radius={[4, 4, 0, 0]} />
                            <Bar dataKey="Refuelled" fill="#73B03A" radius={[4, 4, 0, 0]} />
                            <Bar dataKey="Drained" fill="#EB7283" radius={[4, 4, 0, 0]} />
                          </BarChart>
                        </ResponsiveContainer>
                      )}
                    </Card>
                  </div>
                </Spin>
              </Col>
              <Col sm={24} md={24} className="mt-3">
                <Card title="Fuel by Vehicle" bordered={true}>
                  <StyledTable
                    loading={insightsLoading}
                    dataSource={insVehicles}
                    columns={vehicleColumns}
                    pagination={{ pageSize: 10, hideOnSinglePage: true, size: "small" }}
                    scroll={{ x: "max-content" }}
                  />
                </Card>
              </Col>
              <Col sm={24} md={24} className="mt-3">
                <Card title="Refuelling & Fuel Drain Events" bordered={true}>
                  <StyledTable
                    loading={insightsLoading}
                    dataSource={insEvents}
                    columns={eventColumns}
                    pagination={{ pageSize: 10, hideOnSinglePage: true, size: "small" }}
                    scroll={{ x: "max-content" }}
                    locale={{ emptyText: "No refuel or drain events in this period" }}
                  />
                </Card>
              </Col>
            </>
          )}
          <br />
          <Col sm={24} md={24} bordered={true} className="Fuel-List mt-3">
            <Spin spinning={loading}>
              <div className="fuel-totals" style={{ display: "flex", justifyContent: "space-around", textAlign: "center", backgroundColor: "#e3e3e3ff" }}>
                {FuelDetailsData.map((item, index) => (
                  <div key={index} className="fuel-report-item">
                    <Stack direction="horizontal">
                      <div className="text-content">
                        <h6>{item.Name}</h6>
                        <h2>{item.values}</h2>
                      </div>
                      {index < 1 && <div className="vr" />}
                    </Stack>
                  </div>
                ))}
              </div>
            </Spin>
          </Col> */}
          <br />
          <Col sm={24} md={24} className="mt-3">
            {/* <div className="section-title">Fuel Entries</div> */}
            <Spin spinning={loading}>
              <div id="Export Chart">
                <Card bordered={true}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "10px" }}>
                    <h6>Fuel Consumed By Vehicle</h6>
                    {exportIcon("Export Chart", "Fuel Report")}
                  </div>
                  {donut(vehicleData, "FuelQty", COLORS, vehicleLabel, 200, 120, "40px")}
                  {legendList(vehicleData, "VEHNAME", "FuelQty", COLORS)}
                </Card>
              </div>
            </Spin>
          </Col>
          <Col sm={24} md={24} className="mt-3">
            <Spin spinning={loading}>
              <div id="Export Chart1">
                <Card bordered={true}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "10px" }}>
                    <h6>Fuel Consumed By Driver</h6>
                    {exportIcon("Export Chart1", "Fuel Consumed By Driver")}
                  </div>
                  {donut(driverData, "VAL", COLORS1, driverLabel, 180, 100, "30px")}
                  {legendList(driverData, "DATA", "VAL", COLORS1)}
                </Card>
              </div>
            </Spin>
          </Col>
          <Col sm={24} xl={12} md={12} className="mt-3">
            <Card title="Fuel Stations" bordered={true}>
              <StyledTable
                dataSource={stationData}
                columns={FuelsColumn}
                pagination={false}
                scroll={{ x: "max-content", y: 400 }}
              />
            </Card>
          </Col>
          <Col xl={12} sm={24} md={12} className="mt-3">
            <div id="Export Chart2">
              <Card
                title="Top 5 Stations Fueled"
                bordered={true}
                className="custome-card"
                extra={
                  <div style={{ display: "flex", alignItems: "center", gap: "3px" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "10px" }}>
                      {exportIcon("Export Chart2", "Top 5 Stations Fueled")}
                    </div>
                  </div>
                }
              >
                <FuelVerticalChart />
              </Card>
            </div>
          </Col>
        </Row>
      </Fuelreportstyle>
    </>
  );
};

export default withNexusReportShell(NexusFuelReport);
